// src/utils/errorHandler.js

// Error message mappings for user-friendly display
export const ERROR_MESSAGES = {
  // Authentication errors
  INVALID_CREDENTIALS: 'Invalid username or password. Please check your credentials and try again.',
  ACCOUNT_LOCKED: 'Your account has been locked due to multiple failed login attempts. Please contact support.',
  ACCOUNT_DISABLED: 'Your account has been disabled. Please contact support for assistance.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  INVALID_TOKEN: 'Your session is invalid. Please log in again.',
  
  // Authorization errors
  ACCESS_DENIED: 'You do not have permission to perform this action.',
  INSUFFICIENT_PERMISSIONS: 'You do not have sufficient permissions to access this resource.',
  
  // User account errors
  USERNAME_EXISTS: 'An account with this email address already exists.',
  WEAK_PASSWORD: 'Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character.',
  EMAIL_NOT_VERIFIED: 'Please verify your email address before logging in.',
  VERIFICATION_TOKEN_EXPIRED: 'Email verification link has expired. Please request a new verification email.',
  VERIFICATION_TOKEN_INVALID: 'Invalid verification link. Please check your email for the correct link.',
  
  // Data validation errors
  REQUIRED_FIELD_MISSING: 'Please fill in all required fields.',
  INVALID_EMAIL_FORMAT: 'Please enter a valid email address.',
  INVALID_DATE_FORMAT: 'Please enter a valid date.',
  INVALID_PHONE_FORMAT: 'Please enter a valid phone number.',
  DUPLICATE_ENTRY: 'This record already exists in the system.',
  
  // Business logic errors
  INSUFFICIENT_BALANCE: 'Insufficient account balance for this transaction.',
  TRANSACTION_LIMIT_EXCEEDED: 'Transaction amount exceeds your daily limit.',
  CHEQUE_NUMBER_EXISTS: 'This cheque number has already been used.',
  BENEFICIARY_NOT_FOUND: 'The specified beneficiary was not found.',
  BANK_ACCOUNT_NOT_FOUND: 'The specified bank account was not found.',
  TEMPLATE_NOT_FOUND: 'The specified cheque template was not found.',
  
  // System errors
  SERVER_ERROR: 'A server error occurred. Please try again later.',
  DATABASE_ERROR: 'A database error occurred. Please try again later.',
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
  TIMEOUT_ERROR: 'The request timed out. Please try again.',
  SERVICE_UNAVAILABLE: 'The service is temporarily unavailable. Please try again later.',
  
  // File upload errors
  FILE_TOO_LARGE: 'File size is too large. Please upload a smaller file.',
  INVALID_FILE_TYPE: 'Invalid file type. Please upload a supported file format.',
  UPLOAD_FAILED: 'File upload failed. Please try again.',
  
  // Default fallback
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

// HTTP status code to user message mapping
export const HTTP_STATUS_MESSAGES = {
  400: 'Bad request. Please check your input and try again.',
  401: 'Invalid username or password. Please check your credentials.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'This record already exists in the system.',
  422: 'Please check your input and fix any validation errors.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'A server error occurred. Please try again later.',
  502: 'Service temporarily unavailable. Please try again later.',
  503: 'Service temporarily unavailable. Please try again later.',
  504: 'The request timed out. Please try again.',
};

// Specific error code mappings based on API documentation
export const API_ERROR_CODES = {
  // Auth specific
  'AUTH_INVALID_CREDENTIALS': ERROR_MESSAGES.INVALID_CREDENTIALS,
  'AUTH_ACCOUNT_LOCKED': ERROR_MESSAGES.ACCOUNT_LOCKED,
  'AUTH_ACCOUNT_DISABLED': ERROR_MESSAGES.ACCOUNT_DISABLED,
  'AUTH_SESSION_EXPIRED': ERROR_MESSAGES.SESSION_EXPIRED,
  'AUTH_EMAIL_NOT_VERIFIED': ERROR_MESSAGES.EMAIL_NOT_VERIFIED,
  
  // User management
  'USER_USERNAME_EXISTS': ERROR_MESSAGES.USERNAME_EXISTS,
  'USER_WEAK_PASSWORD': ERROR_MESSAGES.WEAK_PASSWORD,
  'USER_EMAIL_VERIFICATION_EXPIRED': ERROR_MESSAGES.VERIFICATION_TOKEN_EXPIRED,
  'USER_EMAIL_VERIFICATION_INVALID': ERROR_MESSAGES.VERIFICATION_TOKEN_INVALID,
  
  // Business logic
  'CHEQUE_NUMBER_EXISTS': ERROR_MESSAGES.CHEQUE_NUMBER_EXISTS,
  'BENEFICIARY_NOT_FOUND': ERROR_MESSAGES.BENEFICIARY_NOT_FOUND,
  'BANK_ACCOUNT_NOT_FOUND': ERROR_MESSAGES.BANK_ACCOUNT_NOT_FOUND,
  'TEMPLATE_NOT_FOUND': ERROR_MESSAGES.TEMPLATE_NOT_FOUND,
  'INSUFFICIENT_BALANCE': ERROR_MESSAGES.INSUFFICIENT_BALANCE,
  'TRANSACTION_LIMIT_EXCEEDED': ERROR_MESSAGES.TRANSACTION_LIMIT_EXCEEDED,
  
  // Access control
  'MODULE_ACCESS_DENIED': "You don't have access to this module. Contact your administrator.",
  'FEATURE_DISABLED': 'This feature is not enabled for your company. Ask your administrator to enable it in Preferences.',

  // System
  'DATABASE_ERROR': ERROR_MESSAGES.DATABASE_ERROR,
  'NETWORK_ERROR': ERROR_MESSAGES.NETWORK_ERROR,
  'SERVICE_UNAVAILABLE': ERROR_MESSAGES.SERVICE_UNAVAILABLE,
};

/**
 * Get user-friendly error message from API error response
 * @param {Object} error - The error object from API response
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error) => {
  // Check if error has a response (HTTP error)
  if (error.response) {
    const { status, data } = error.response;
    
    // First, check if API returned a specific error code (backend sends PascalCase ErrorCode)
    const apiErrorCode = data && (data.errorCode || data.ErrorCode);
    if (apiErrorCode && API_ERROR_CODES[apiErrorCode]) {
      return API_ERROR_CODES[apiErrorCode];
    }
    
    // Check if API returned a specific error message
    if (data && data.message) {
      // First priority: If it's a reasonably user-friendly message, return it as-is
      if (data.message.length < 150 && 
          !data.message.includes('Error:') && 
          !data.message.includes('Exception:') &&
          !data.message.includes('Stack trace') &&
          !data.message.includes('at ') &&
          data.message.trim().length > 0) {
        return data.message;
      }
      
      // Second priority: Map common technical API messages to user-friendly ones
      const message = data.message.toLowerCase();
      
      if (message.includes('unauthorized') || message.includes('401')) {
        return ERROR_MESSAGES.INVALID_CREDENTIALS;
      }
      if (message.includes('forbidden') || message.includes('403')) {
        return ERROR_MESSAGES.ACCESS_DENIED;
      }
      if (message.includes('not found') || message.includes('404')) {
        return 'The requested resource was not found.';
      }
      if (message.includes('internal server error') || message.includes('500')) {
        return ERROR_MESSAGES.SERVER_ERROR;
      }
      if (message.includes('bad request') || message.includes('400')) {
        return 'Please check your input and try again.';
      }
      if (message.includes('email') && message.includes('exists')) {
        return ERROR_MESSAGES.USERNAME_EXISTS;
      }
      if (message.includes('not verified')) {
        return ERROR_MESSAGES.EMAIL_NOT_VERIFIED;
      }
      if (message.includes('expired')) {
        return ERROR_MESSAGES.VERIFICATION_TOKEN_EXPIRED;
      }
      if (message.includes('duplicate')) {
        return ERROR_MESSAGES.DUPLICATE_ENTRY;
      }
    }
    
    // Third priority: Check for other message properties
    if (data && (data.Message || data.ErrorMessage || data.error)) {
      const altMessage = data.Message || data.ErrorMessage || data.error;
      if (typeof altMessage === 'string' && altMessage.length < 150 && altMessage.trim().length > 0) {
        return altMessage;
      }
    }
    
    // Fall back to HTTP status code mapping
    if (HTTP_STATUS_MESSAGES[status]) {
      return HTTP_STATUS_MESSAGES[status];
    }
    
    return `An error occurred (${status}). Please try again.`;
  }
  
  // Network error
  if (error.request) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }
  
  // Other error
  if (error.message) {
    const message = error.message.toLowerCase();
    
    if (message.includes('network')) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }
    if (message.includes('timeout')) {
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    }
    if (message.includes('connection')) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }
  }
  
  return ERROR_MESSAGES.UNKNOWN_ERROR;
};

/**
 * Format validation errors for display
 * @param {Array} validationErrors - Array of validation error objects
 * @returns {string} - Formatted error message
 */
export const formatValidationErrors = (validationErrors) => {
  if (!validationErrors || !Array.isArray(validationErrors) || validationErrors.length === 0) {
    return ERROR_MESSAGES.REQUIRED_FIELD_MISSING;
  }
  
  if (validationErrors.length === 1) {
    return validationErrors[0].message || validationErrors[0].Message || 'Please fix the validation error.';
  }
  
  return `Please fix the following errors: ${validationErrors.map(err => err.message || err.Message).join(', ')}`;
};

/**
 * Handle API error and return standardized error object
 * @param {Object} error - The error object from API
 * @returns {Object} - Standardized error response object
 */
export const handleApiError = (error) => {
  console.error('🚨 API Error:', error);
  
  const errorMessage = getErrorMessage(error);
  const status = error.response?.status || null;
  const errorCode = error.response?.data?.errorCode || error.response?.data?.ErrorCode || null;
  const validationErrors = error.response?.data?.validationErrors || error.response?.data?.ValidationErrors || null;
  
  return {
    success: false,
    message: errorMessage,
    errorCode,
    validationErrors,
    status,
    data: null
  };
};

export default {
  getErrorMessage,
  formatValidationErrors,
  handleApiError,
  ERROR_MESSAGES,
  HTTP_STATUS_MESSAGES,
  API_ERROR_CODES
}; 